/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import { tagName } from '@ember-decorators/component';
import onTrackGrades, {
  isGradeSet,
  levelLabel,
  subjectLevels,
} from 'linced-app/utils/on-track-grades';
import { escapeHtml } from 'linced-app/utils/reporting';

/**
 * Insights::OnTrackCalculation lets a user see and change the Progression judgements a
 * learner must reach to count as "on track" for this Subject's Milestones.
 *
 * Two grades are stored on the Subject: onTrackInterimGrade applies to interim
 * Milestones, onTrackFullGrade to full ones. An
 * interim grade of UNSPECIFIED means one rule covers both, which is what the
 * "use a different rule" switch toggles.
 *
 * @param {Ember.Model<goals/subject>} subject - the Subject being viewed.
 */
@tagName('')
export default class OnTrackCalculation extends Component {
  @service
  logging;

  @service
  permissions;

  @service
  session;

  @service
  toaster;

  /** @type {Ember.Model<goals/subject>|null} */
  subject = null;

  /** @type {boolean} True while a save is in flight. */
  @tracked saving = false;

  // The rules are Subject configuration, so only those who can reach the
  // Subject settings (school admins and up) may change them. Everyone else
  // sees the rule in force, read-only.
  @computed('session.scopes.[]')
  get canEdit() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  /**
   * Whether interim Milestones get their own rule. Seeded from the Subject
   * rather than bound to it: an unconfigured Subject and one deliberately set
   * to a single rule both have no interim grade, but should show the switch
   * differently. Defaults on.
   *
   * @type {boolean}
   */
  @tracked usesSeparateRule = true;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let subjectId = this.subject?.id;
    if (this._seededFor === subjectId) {
      return;
    }
    this._seededFor = subjectId;
    this.usesSeparateRule = this._defaultUsesSeparateRule();
  }

  // gradeOptions lists the progress levels a rule can be set to: the levels
  // this school has defined for the Subject, apart from the lowest. That one is
  // deliberately not offered - declaring it on track would make the measure
  // meaningless, since every learner with a judgement would qualify.
  @computed('subject.judgementGrades.[]')
  get gradeOptions() {
    let levels = subjectLevels(this.subject).slice(1);
    let highestIndex = levels.length - 1;
    return (
      levels
        .map((grade, index) => {
          // Nothing sits above the top level, so "or above" would misdescribe it.
          let qualifier = index === highestIndex ? 'only' : 'or above';
          let name = levelLabel(this.subject, grade.value);
          return {
            value: grade.value,
            // name is the level's wording (the Subject's success criterion
            // or the school's term), named as the rest of the page names it;
            // label is how the rule reads, and is what the dropdowns and
            // summary show.
            name,
            qualifier,
            label: `${name} ${qualifier}`,
          };
        })
        // Highest first, so the dropdowns list the levels top down the way the
        // tables do.
        .reverse()
    );
  }

  // Nothing stored means the tables count against the default rule, so the
  // card shows that same effective rule rather than reading as unconfigured. isDefaulted keeps
  // the distinction visible: this is what applies, not what anyone chose.
  @computed('subject.{onTrackInterimGrade,onTrackFullGrade}')
  get isDefaulted() {
    return !this._isSet(this.subject?.onTrackFullGrade);
  }

  // The grades the tables count against, defaults included. Shared with the
  // progress overview controller so the card and the tables always agree.
  @computed('subject.{onTrackInterimGrade,onTrackFullGrade,judgementGrades.[]}')
  get effectiveGrades() {
    return onTrackGrades(this.subject);
  }

  @reads('effectiveGrades.full')
  effectiveFullGrade;

  @reads('effectiveGrades.interim')
  effectiveInterimGrade;

  // The rule currently in force, defaulted or not. The summary and the
  // description use these, so they always describe what the tables below are
  // counting.
  @computed('gradeOptions.[]', 'effectiveInterimGrade')
  get effectiveInterim() {
    return this._optionFor(this.effectiveInterimGrade);
  }

  @computed('gradeOptions.[]', 'effectiveFullGrade')
  get effectiveFull() {
    return this._optionFor(this.effectiveFullGrade);
  }

  // What the dropdowns bind to. Left unset while defaulting, so an untouched
  // Subject reads as "nothing chosen here yet" rather than implying the
  // fallback was a decision someone made.
  @computed('isDefaulted', 'effectiveInterim')
  get selectedInterim() {
    return this.isDefaulted ? null : this.effectiveInterim;
  }

  @computed('isDefaulted', 'effectiveFull')
  get selectedFull() {
    return this.isDefaulted ? null : this.effectiveFull;
  }

  // summary is the one-line description shown next to the title, and is the
  // only thing visible while collapsed.
  @computed(
    'isDefaulted',
    'effectiveFull.label',
    'effectiveInterim.label',
    'usesSeparateRule',
  )
  get summary() {
    let full = this.effectiveFull?.label;
    if (isEmpty(full)) {
      return 'Not set';
    }
    // Each rule carries its own marker, so neither reads as chosen. Level
    // names are school-configured, so they are escaped before going into
    // the summary's markup.
    let mark = (label) =>
      this.isDefaulted
        ? `${escapeHtml(label)} <strong>(default)</strong>`
        : escapeHtml(label);

    if (!this.usesSeparateRule) {
      return htmlSafe(`${mark(full)} (interim and full)`);
    }
    let interim = this.effectiveInterim?.label;
    // Each rule on its own line: with the level names and a default marker
    // the two together are too long to read as one line.
    return htmlSafe(
      `<strong>Interim - on track to meet:</strong> ${mark(
        interim,
      )}<br><strong>Full - met:</strong> ${mark(full)}`,
    );
  }

  // An interim grade means separate rules. Without one, a Subject that has a
  // full grade was deliberately put on a single rule; a Subject with neither
  // has never been configured, and starts with the switch on.
  _defaultUsesSeparateRule() {
    if (this._isSet(this.subject?.onTrackInterimGrade)) {
      return true;
    }
    return !this._isSet(this.subject?.onTrackFullGrade);
  }

  _isSet(grade) {
    return isGradeSet(grade);
  }

  _toGrade(grade) {
    return this._isSet(grade) ? grade : ProgressionJudgement.UNSPECIFIED;
  }

  _optionFor(grade) {
    if (!this._isSet(grade)) {
      return null;
    }
    return findBy(this.gradeOptions, 'value', grade);
  }

  /**
   * _persist reloads the Subject before writing, so a change made elsewhere
   * since this page loaded is not clobbered, then saves both grades together.
   *
   * @param {number} interimGrade - UNSPECIFIED when one rule covers both.
   * @param {number} fullGrade
   */
  async _persist(interimGrade, fullGrade) {
    if (!this.canEdit) {
      return;
    }
    let subject = this.subject;
    this.saving = true;

    try {
      await subject.reload();
      // Normalise on the way out: a Subject saved before these fields
      // existed carries null, and the API's enum has no such value.
      subject.onTrackInterimGrade = this._toGrade(interimGrade);
      subject.onTrackFullGrade = this._toGrade(fullGrade);
      await subject.save();
    } catch (error) {
      this.logging.pushError(error, 'onTrackCalculation:persist');
      rollbackRecord(subject);
      this.usesSeparateRule = this._defaultUsesSeparateRule();
      this.toaster.error('Unable to save how on track is calculated.');
    } finally {
      if (!this.isDestroyed && !this.isDestroying) {
        this.saving = false;
      }
    }
  }

  // Each change saves the effective value of the other grade alongside it, so
  // choosing one rule on a defaulted Subject keeps the other at its default
  // rather than dropping it.
  @action
  setInterimGrade(grade) {
    this._persist(grade, this.effectiveFullGrade);
  }

  @action
  setFullGrade(grade) {
    this._persist(
      this.usesSeparateRule
        ? this.effectiveInterimGrade
        : ProgressionJudgement.UNSPECIFIED,
      grade,
    );
  }

  // Turning the switch off drops the interim grade, leaving the full grade
  // to cover both kinds of Milestone. Turning it on seeds the interim grade
  // from the full one so the rule stays equivalent until it is changed.
  @action
  setUsesSeparateRule(useSeparate) {
    this.usesSeparateRule = useSeparate;
    let fullGrade = this.effectiveFullGrade;
    this._persist(
      useSeparate ? fullGrade : ProgressionJudgement.UNSPECIFIED,
      fullGrade,
    );
  }
}
